/**
 * MentorHero Component
 * 
 * Hero section for Home page showing mentor + book of the day
 */

import React from 'react';
import { View, StyleSheet, Pressable, Image, ActivityIndicator } from 'react-native';
import { Text, Theme } from '../../../components/ui';
import { FireBook } from '../../../models/FirestoreModels';

interface MentorHeroProps {
  book: FireBook | null;
  bookReason?: string;
  onMentorPress: () => void;
  onBookPress: () => void;
  loading?: boolean;
}

export const MentorHero: React.FC<MentorHeroProps> = ({
  book,
  bookReason = 'Your free book today',
  onMentorPress,
  onBookPress,
  loading = false,
}) => {
  console.log('MentorHero render:', { hasBook: !!book, bookTitle: book?.title, loading });
  
  return (
    <View style={styles.outerContainer}>
      <View style={styles.headerSection}>
        <Text variant="h3" style={styles.sectionTitle}>
          Free Daily
        </Text>
      </View>
      
      {/* Single Book Card - Full Width */}
      {(book || loading) && (
        <View style={styles.bookCard}>
            {loading ? (
              <View style={styles.bookLoading}>
                <ActivityIndicator size="small" color={Theme.colors.primary} />
                <Text variant="caption" color="secondary" style={styles.loadingText}>
                  Loading today's pick...
                </Text>
              </View>
            ) : book ? (
              <Pressable
                style={({ pressed }) => [
                  styles.bookCardPressable,
                  pressed && styles.bookCardPressed,
                ]}
                onPress={onBookPress}
              >
                <View style={styles.bookCardContent}>
                  {/* Book Cover - Left */}
                  <Image
                    source={{ uri: book.coverImageUrl || book.coverImageFullUrl }}
                    style={styles.bookCover}
                    resizeMode="cover"
                  />
                  
                  {/* Book Info - Right */}
                  <View style={styles.bookInfo}>
                    {/* Badge */}
                    <View style={styles.freeBadge}>
                      <Text style={styles.freeBadgeText}>⭐ FREE TODAY</Text>
                    </View>
                    
                    {/* Title and Author */}
                    <Text numberOfLines={2} style={styles.bookTitle}>
                      {book.shortTitle || book.title}
                    </Text>
                    <Text numberOfLines={1} style={styles.bookAuthor}>
                      {book.author}
                    </Text>
                  </View>
                </View>
              </Pressable>
            ) : null}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  outerContainer: {
    marginBottom: 40,
  },
  headerSection: {
    paddingHorizontal: Theme.spacing.lg,
    marginBottom: Theme.spacing.md,
    borderLeftWidth: 4,
    borderLeftColor: Theme.colors.primary,
    paddingLeft: Theme.spacing.md,
  },
  sectionTitle: {
    marginBottom: 4,
    color: Theme.colors.sectionHeader,
  },
  sectionSubtitle: {
    lineHeight: 18,
    color: 'rgb(0, 71, 89)',
  },
  bookCard: {
    backgroundColor: '#E8F9F3', // Exact color from Figma
    borderRadius: 16,
    marginHorizontal: Theme.spacing.lg,
    ...Theme.shadows.md,
  },
  bookCardPressable: {
    padding: 16,
  },
  bookCardPressed: {
    opacity: 0.98,
    transform: [{ scale: 1.02 }],
  },
  bookCardContent: {
    flexDirection: 'row',
    gap: 12,
  },
  bookCover: {
    width: 60, // Smaller book cover
    height: 90,
    borderRadius: 8,
    backgroundColor: Theme.colors.border,
    flexShrink: 0,
    ...Theme.shadows.md,
  },
  bookInfo: {
    flex: 1,
    justifyContent: 'flex-start',
  },
  freeBadge: {
    alignSelf: 'flex-start',
    backgroundColor: '#00C27B', // Exact green from Figma
    paddingHorizontal: 10,
    paddingVertical: 3,
    borderRadius: 999,
    marginBottom: 6,
  },
  freeBadgeText: {
    color: '#FFFFFF',
    fontSize: 10,
    fontWeight: '700',
    fontFamily: Theme.typography.fontFamily.bold,
  },
  bookTitle: {
    fontSize: 14,
    lineHeight: 18,
    fontWeight: '600',
    fontFamily: Theme.typography.fontFamily.semiBold,
    color: 'rgb(0, 71, 89)',
    marginBottom: 2,
  },
  bookAuthor: {
    fontSize: 12,
    color: 'rgba(0, 71, 89, 0.7)',
  },
  bookLoading: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: Theme.spacing.lg,
  },
  loadingText: {
    marginTop: Theme.spacing.sm,
  },
});

